import { useState, useEffect, memo } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { yupResolver } from '@hookform/resolvers/yup';
import client from '../../../client';
import * as yup from 'yup';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import FuseMessage from '@fuse/core/FuseMessage';
import { useDispatch } from 'react-redux';
import { showMessage } from 'app/store/fuse/messageSlice';
import { FormControl, Select, MenuItem, Box, Chip, FormControlLabel, Checkbox, Tooltip, IconButton, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ModalCreateContentType from './ModalCreateContentType';
import { useTranslation } from 'react-i18next';

const schema = yup.object().shape({
  capacity: yup.number().required('Capacity is required'),
  kind_of_content: yup.string().required('Kind of Content is required'),
  location: yup.string().nullable(),
  notes: yup.string().nullable(),
  show_in_dashboard: yup.boolean(),
});

function ModalCreateFuelTank({ isOpen, setIsOpen, onCreated, _fuelnLubeCatalog }) {
  const { t } = useTranslation('navigation');
  const dispatch = useDispatch();
  const [open, setOpen] = useState(isOpen);
  const [contentTypeModalOpen, setContentTypeModalOpen] = useState(false);
  const [fuelnLubeCatalog, setFuelnLubeCatalog] = useState([]);
  const [form, setForm] = useState({
    compacctnum: '',
    capacity: '',
    kind_of_content: '',
    location: '',
    notes: '',
    show_in_dashboard: true,
  });

  useEffect(() => {
    setOpen(isOpen);
    setFuelnLubeCatalog(_fuelnLubeCatalog || []);
  }, [isOpen]);

  const handleClose = () => {
    setOpen(false);
    setIsOpen(false);
  };


  // Obtener compacctnum de localStorage
  let compacctnum = '';
  try {
    const lSData = JSON.parse(localStorage.getItem('persist:root'));
    const userData = JSON.parse(lSData.loadz_auth).AuthReducer.user;
    compacctnum = userData.company.compacctnum;
  } catch (e) {
    compacctnum = '';
  }


  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      compacctnum: compacctnum,
      capacity: '',
      kind_of_content: '',
      location: '',
      notes: '',
      show_in_dashboard: true,
    },
    resolver: yupResolver(schema),
  });

  const clearForm = {
    compacctnum: compacctnum,
    capacity: '',
    kind_of_content: '',
    location: '',
    notes: '',
    show_in_dashboard: true,
  };

  const _showMessage = (msg, color) => {
    dispatch(
      showMessage({
        message: msg,
        autoHideDuration: 6000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: color,
      })
    );
  };

  const { formState, getValues, reset, control } = methods;
  const { isValid, errors } = formState;

  const handleCreateFuelTank = async () => {
    try {
      const data = JSON.parse(JSON.stringify(getValues()));
      data.capacity = parseFloat(data.capacity);
      data.compacctnum = compacctnum;
      data.kind_of_content =  _fuelnLubeCatalog.find(fuel => fuel.product_name === form.kind_of_content)?.product_id;
      const response = await client.post('/fuel-inventory/create-fuel-tank/', data);
      if (response.data.message) {
        _showMessage(response.data.message, 'error');
        return;
      }
      if (onCreated) onCreated(response.data);
      _showMessage(t('FUEL_TANK_CREATED'), 'success');
      setOpen(false);
      reset(clearForm);
      setIsOpen(false);
      if (onCreated) onCreated();
    } catch (error) {
      _showMessage(t('SOMETHING_WENT_WRONG'), 'error');
    }
  };

  const handleContentTypeCreated = (newContentType) => {
    // Refresh the fuel catalog after creating a new content type
    if(newContentType.data){
      setFuelnLubeCatalog(newContentType.data);
    }
    if (onCreated) onCreated();
    setContentTypeModalOpen(false);
    setForm({ ...form, kind_of_content: newContentType.data.product_name });
  };

  return (
    <div>
      <FuseMessage />
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{t('CREATE_NEW_TANK')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('COMPLETE_FORM_CREATE_TANK')}
          </DialogContentText>
          <br />
          <FormProvider {...methods}>
            <div className="flex flex-col gap-4">
              {/* compacctnum is obtained automatically and not shown to the user */}
              <label>
                {t('CAPACITY')}
              </label>
              <Controller
                name="capacity"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    required
                    error={!!errors.capacity}
                    helperText={errors?.capacity?.message}
                    variant="outlined"
                    fullWidth
                    inputProps={{ style: { padding: '5px' } }}
                  />
                )}
              />
              <label>
                {t('TANK_CONTENTS')}
              </label>
              <FormControl fullWidth margin="normal">
                <Select
                  value={form.kind_of_content}
                  label={t('TANK_CONTENTS')}
                  onChange={(event) => {
                    if (event.target.value === 'add_new') {
                      setContentTypeModalOpen(true);
                    } else {
                      setForm({ ...form, kind_of_content: event.target.value });
                    }
                  }}
                >
                  {_fuelnLubeCatalog.map((fuel) => (
                    <MenuItem key={fuel.id} value={fuel.product_name}>
                      {fuel.product_name}
                    </MenuItem>
                  ))}
                  <MenuItem value="add_new" sx={{ 
                    borderTop: '1px solid #e0e0e0',
                    backgroundColor: '#f5f5f5',
                    '&:hover': {
                      backgroundColor: '#e3f2fd'
                    }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label="+" 
                        size="small" 
                        sx={{ 
                          backgroundColor: '#1976d2', 
                          color: 'white',
                          fontSize: '12px',
                          height: '20px'
                        }} 
                      />
                      {t('ADD_NEW_CONTENT_TYPE')}
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              <label>
                {t('TANK_NICKNAME')}
              </label>
              <Controller
                name="location"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.location}
                    helperText={errors?.location?.message}
                    variant="outlined"
                    fullWidth
                    inputProps={{ style: { padding: '5px' } }}
                  />
                )}
              />
              <label>
                {t('NOTES')}
              </label>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.notes}
                    helperText={errors?.notes?.message}
                    variant="outlined"
                    fullWidth
                    inputProps={{ style: { padding: '5px' } }}
                  />
                )}
              />
              <Controller
                name="show_in_dashboard"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>
                          {t('SHOW_IN_DASHBOARD')}
                        </Typography>
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {t('SHOW_IN_DASHBOARD_EXPLANATION')}
                          </Typography>
                          <Box
                            component="img"
                            src="/assets/images/dashboard-fuel-chart.png"
                            alt="Dashboard Fuel Chart"
                            sx={{
                              width: '80%',
                              maxWidth: '300px',
                              height: 'auto',
                              borderRadius: '8px',
                              border: '2px solid rgba(255, 255, 255, 0.1)',
                              mt: 1,
                            }}
                            onError={(e) => {
                              // Fallback si la imagen no existe
                              e.target.style.display = 'none';
                            }}
                          />
                        </Box>
                      }
                      arrow
                      placement="top"
                      sx={{
                        '& .MuiTooltip-tooltip': {
                          maxWidth: '550px',
                          backgroundColor: 'rgba(0, 0, 0, 0.95)',
                        }
                      }}
                    >
                          <IconButton size="small" sx={{ color: '#1976d2' }}>
                            <InfoIcon sx={{ fontSize: '1.2rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  />
                )}
              />
            </div>
          </FormProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('CANCEL')}</Button>
          <Button onClick={handleCreateFuelTank} variant="contained">
            {t('CREATE')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Content Type Creation Modal */}
      <ModalCreateContentType
        isOpen={contentTypeModalOpen}
        setIsOpen={setContentTypeModalOpen}
        onCreated={handleContentTypeCreated}
      />
    </div>
  );
}

export default memo(ModalCreateFuelTank);
